import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import cn from 'classnames';
import { debounce } from 'lodash';

import emailSentIco from 'assets/images/email-new-pin-ico.png';
import IMG_SLICK from 'assets/images/logos/slick-logo-text.png';
import validateEmail from 'bl/validation/validateEmail.bl';
import Space from 'components/generic/Space.component';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { xeLogoutUser } from 'utils/cognito.util';
import withDefaultPrevented from 'utils/dom/withDefaultPrevented';
import { isEnvMobile } from 'utils/env/determine.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

import { clearLockError } from 'lib/actions';
import PinModalComponent from 'onboarding/common/PinModal';
import SlickSlider from 'user/common/SlickSlider';

import CN from './PinForm.module.scss';

const L = logger('PinForm');

const determinePinLength = ({ state }) => (state.pinCode || []).length || 0;

const PIN_LIMIT = 4;
const DEBOUNCE_TIME = 100;

const PinForm = ({ onPin }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const lockError = useSelector((state) => state?.lib?.lockError);

  const [pinCode, setPinCode] = useState([]);
  const { val: requestWithEmailModal, nay: hideRequestEmailModal } = useTip(false);
  const { val: emailSentModal, yay: showEmailSentModal, nay: hideEmailSentModal } = useTip(false);
  const [inputValue, setInputValue] = useState('');
  const { val: emailHasError, set: setEmailHasError } = useTip(false);

  const keyboardRef = useRef(null);
  const componentRef = useRef(null);

  // We need refs for lockError and onPin so the debounced handler always sees current values
  const lockErrorRef = useRef(lockError);
  lockErrorRef.current = lockError;
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  useEffect(() => {
    if (componentRef.current) {
      componentRef.current.focus();
    }
  }, []);

  const handlePress = useMemo(
    () =>
      debounce((num) => {
        setPinCode((prevPinCode) => {
          const pinLength = (prevPinCode || []).length || 0;
          const redo = pinLength >= PIN_LIMIT && !!lockErrorRef.current;

          if (redo) {
            dispatch(clearLockError());
          }

          const newPinCode = [...(redo ? [] : prevPinCode), String(num)];

          if (newPinCode.length >= PIN_LIMIT) {
            onPinRef.current(newPinCode.join(''));
          }

          return newPinCode;
        });
      }, DEBOUNCE_TIME),
    [dispatch],
  );

  const handleRemove = useCallback(() => {
    setPinCode((prevPinCode) => {
      const newPinCode = [...prevPinCode];
      newPinCode.pop();
      return newPinCode;
    });
  }, []);

  const handleRemoveAll = useCallback(() => {
    setPinCode([]);
  }, []);

  const handleLogout = useMemo(() => withDefaultPrevented(() => void dispatch(xeLogoutUser())), [dispatch]);

  const handleClose = useCallback(() => void (hideRequestEmailModal(), hideEmailSentModal()), [hideRequestEmailModal, hideEmailSentModal]);

  const handleEmailChange = useCallback((ev) => void setInputValue(ev.target.value), []);

  const handleGoToLogin = useCallback(() => void history.push('/login'), [history]);

  const handleSendEmail = useCallback(() => void (hideRequestEmailModal(), showEmailSentModal()), [hideRequestEmailModal, showEmailSentModal]);

  const handleValidateEmail = useCallback((value) => void setEmailHasError(!validateEmail({ value })), [setEmailHasError]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key.match(/[0-9]/)) {
        handlePress(event.key);
      }
      if (event.key === 'Backspace' || event.key === 'Delete') {
        handleRemove();
      }
    },
    [handlePress, handleRemove],
  );

  const state = { pinCode };
  const pinLength = determinePinLength({ state });
  const wrongPin = lockError && pinLength >= PIN_LIMIT;
  const isMobile = isEnvMobile({ regex: true });

  L.debug('render()', { isMobile, pinLength, wrongPin, keyboard: keyboardRef.current }, { lockError, pinCode });

  // Prevent pinch-zoom on mobile keyboard
  useEffect(() => {
    const keyboard = keyboardRef.current;
    if (!isMobile || !keyboard) return;

    const handler = (e) => void (e.scale !== 1 && e.preventDefault());
    keyboard.addEventListener('touchmove', handler, { passive: false });

    return () => keyboard.removeEventListener('touchmove', handler);
  }, [isMobile]);

  const containerClasses = [CN[`code-${pinLength}`]];

  const matchDotsClasses = cn({
    [CN.dotsContainer]: true,
    [CN.dotError]: wrongPin,
  });
  // S.empty stops React from complaining it got false instead of ''
  const emailLabelClasses = cn(S.empty, {
    'input-error': emailHasError,
  });

  return (
    <div data-bem="PinForm" className={CN.component} onKeyDown={handleKeyDown} ref={componentRef} tabIndex="0">
      <div className={CN.layout}>
        <div data-bem="PinForm__content" className={CN.loginColumn}>
          <img src={IMG_SLICK} alt="Slick logo" className={CN.logo} />
          <div className={CN.title}>
            {t('pinForm.title.part1')}
            <span className={CN.bold}>
              <Space>{t('pinForm.title.part2')}</Space>
            </span>
            {S.userLogin}
          </div>

          <div className="login-wrapper">
            <div className="key-board-container" ref={keyboardRef}>
              <div className={containerClasses}>
                <div className={matchDotsClasses}>
                  {[...Array(PIN_LIMIT)].map((value, index) => (
                    <div key={index} className={CN.dotContainer}>
                      <span className={CN.dot} />
                    </div>
                  ))}
                </div>
                <div className={CN.errorContainer}>{wrongPin && <div className={CN.error}>{t('pinForm.enterValidCodeMsg')}</div>}</div>
                <div className={CN.keyboardButtons}>
                  {[...Array(9)].map((value, index) => (
                    <button
                      key={index + 1}
                      onClick={!isMobile ? () => handlePress(index + 1) : alwaysNoop}
                      onTouchStart={isMobile ? () => handlePress(index + 1) : alwaysNoop}
                      className={CN.keyButton}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <div className={CN.emptySpace}></div>
                  <button onClick={!isMobile ? () => handlePress(0) : alwaysNoop} onTouchStart={isMobile ? () => handlePress(0) : alwaysNoop} className={CN.keyButton}>
                    0
                  </button>
                  <button onClick={wrongPin ? handleRemoveAll : handleRemove} className={CN.clearButton} />
                </div>
              </div>
            </div>
          </div>
          <div className={CN.signInContainer}>
            <Link to="#" onClick={handleLogout} className={CN.link}>
              {t('pinForm.emailLoginButton')}
            </Link>
          </div>
        </div>
        <div className={CN.sliderColumn}>
          <SlickSlider />
        </div>
      </div>

      {requestWithEmailModal && (
        <PinModalComponent
          title={t('pinForm.pinModal.title')}
          message=""
          leftButtonText={t('pinForm.pinModal.loginButton')}
          onLeft={handleGoToLogin}
          rightButtonText=""
          onRight={() => {}}
          displayFooter
          displayXclose
          onXclick={
            handleClose // hidden with css
          }
          additionalClass="pin-login-with-email-modal"
        >
          <label data-bem="PinForm__email-form--modal" htmlFor="UserEmail" className={emailLabelClasses} style={{ position: 'relative' }}>
            {emailHasError ? (
              <span className="error" style={{ color: '#FF0F0F' }}>
                {t('pinForm.pinModal.notSlickEmailError')}
              </span>
            ) : (
              <span className="field-name">{t('pinForm.pinModal.emailLabel')}</span>
            )}

            <input
              className="utl-full-width"
              id="UserEmail"
              type="text"
              placeholder={t('pinForm.pinModal.emailPlaceholder')}
              autoComplete="off"
              onChange={handleEmailChange}
              onBlur={handleValidateEmail}
              value={inputValue}
            />
            <button onClick={handleSendEmail} className="input-with-button" />
          </label>
        </PinModalComponent>
      )}

      {emailSentModal && (
        <PinModalComponent
          title={t('pinForm.pinModal.emailSentSuccessful')}
          message=""
          leftButtonText={t('pinForm.pinModal.loginButton')}
          onLeft={alwaysNoop}
          rightButtonText=""
          onRight={alwaysNoop}
          displayFooter
          displayXclose
          onXclick={handleClose}
          additionalClass="pin-login-with-email-modal"
        >
          <div data-bem="PinForm__email-success--modal" className="image-with-text">
            <div className="img-container">
              <img src={emailSentIco} alt="Email sent!" />
            </div>
            <div>
              <p>
                {S.space}
                {t('pinForm.pinModal.resetPin')} {inputValue}
              </p>
            </div>
          </div>
        </PinModalComponent>
      )}
    </div>
  );
};

export default PinForm;
